import React from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { cn } from "./cn";

interface PaginationProps {
  /** Página atual, começando em 1. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

// 1 … 4 5 6 … 10 — sempre a primeira, a última e as vizinhas da atual.
export function pageList(current: number, count: number): Array<number | "…"> {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const start = Math.max(2, Math.min(current - 1, count - 4));
  const end = Math.min(count - 1, Math.max(current + 1, 5));
  const list: Array<number | "…"> = [1];
  if (start > 2) list.push("…");
  for (let p = start; p <= end; p++) list.push(p);
  if (end < count - 1) list.push("…");
  list.push(count);
  return list;
}

const navButton =
  "flex h-9 min-w-[36px] items-center justify-center rounded-control px-2 text-body font-medium transition-colors focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-40";

export const Pagination: React.FC<PaginationProps> = ({ page, pageSize, total, onPageChange, disabled = false }) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <nav aria-label="Paginação" className="flex items-center justify-between gap-3">
      <p className="text-caption text-fg-muted">
        {start}–{end} de {total}
      </p>

      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Página anterior"
          onClick={() => onPageChange(page - 1)}
          disabled={disabled || page <= 1}
          className={cn(navButton, "text-fg-muted enabled:hover:bg-surface-hover")}
        >
          <LuChevronLeft size={18} aria-hidden />
        </button>

        <span className="px-2 text-caption text-fg-muted sm:hidden">
          Página {page} de {pageCount}
        </span>

        <div className="hidden items-center gap-1 sm:flex">
          {pageList(page, pageCount).map((item, i) =>
            item === "…" ? (
              <span key={`gap-${i}`} className="px-1 text-caption text-fg-subtle" aria-hidden>
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                aria-label={`Página ${item}`}
                aria-current={item === page ? "page" : undefined}
                onClick={() => onPageChange(item)}
                disabled={disabled}
                className={cn(
                  navButton,
                  item === page ? "bg-primary-soft text-primary" : "text-fg-muted enabled:hover:bg-surface-hover"
                )}
              >
                {item}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          aria-label="Próxima página"
          onClick={() => onPageChange(page + 1)}
          disabled={disabled || page >= pageCount}
          className={cn(navButton, "text-fg-muted enabled:hover:bg-surface-hover")}
        >
          <LuChevronRight size={18} aria-hidden />
        </button>
      </div>
    </nav>
  );
};
